export const ROLE_IDS = {
  ORGANIZER: 1,
  ADMIN: 2,
  AREA_MANAGER: 3,
  PRACTITIONER_LEAD: 4,
  VISITOR: 5,
  OBSERVER: 6,
  PAYROLL: 7,
  BOOKKEEPER: 8,
  FINANCE_COORDINATOR: 9,
};

export const ROLE_LABELS = {
  1: "Organizer",
  2: "Admin",
  3: "Area Manager",
  4: "Practitioner Lead",
  5: "Visitor",
  6: "Observer",
  7: "Payroll",
  8: "Bookkeeper",
  9: "Finance Coordinator",
};

// Make sure to use this function to get the role id from the user object
function getRoleId(roleOrUser) {
  if (roleOrUser && typeof roleOrUser === "object") return Number(roleOrUser.role);
  const n = Number(roleOrUser);
  return Number.isNaN(n) ? null : n;
}

export function isOrganizer(roleOrUser) {
  return getRoleId(roleOrUser) === ROLE_IDS.ORGANIZER;
}

export function isAdmin(roleOrUser) {
  return getRoleId(roleOrUser) === ROLE_IDS.ADMIN;
}

export function isAreaManager(roleOrUser) {
  return getRoleId(roleOrUser) === ROLE_IDS.AREA_MANAGER;
}

export function isPractitionerLead(roleOrUser) {
  return getRoleId(roleOrUser) === ROLE_IDS.PRACTITIONER_LEAD;
}

export function isVisitor(roleOrUser) {
  return getRoleId(roleOrUser) === ROLE_IDS.VISITOR;
}

export function isObserver(roleOrUser) {
  return getRoleId(roleOrUser) === ROLE_IDS.OBSERVER;
}

export function isPayroll(roleOrUser) {
  return getRoleId(roleOrUser) === ROLE_IDS.PAYROLL;
}

export function isBookkeeper(roleOrUser) {
  return getRoleId(roleOrUser) === ROLE_IDS.BOOKKEEPER;
}

export function isFinanceCoordinator(roleOrUser) {
  return getRoleId(roleOrUser) === ROLE_IDS.FINANCE_COORDINATOR;
}

export function isOrganizerOrAdmin(roleOrUser) {
  return isOrganizer(roleOrUser) || isAdmin(roleOrUser);
}

/** Roles that are scoped to one or more assigned clinics (read or write). */
export function isClinicScopedUser(roleOrUser) {
  return isAreaManager(roleOrUser) || isPractitionerLead(roleOrUser) || isBookkeeper(roleOrUser);
}

/** Roles that have read access across all clinics (no clinic scoping). */
export function canAccessAllClinics(roleOrUser) {
  return (
    isOrganizer(roleOrUser) ||
    isAdmin(roleOrUser) ||
    isObserver(roleOrUser) ||
    isPayroll(roleOrUser) ||
    isFinanceCoordinator(roleOrUser)
  );
}

/** View-only roles (no create/update/delete). */
export function isViewOnlyRole(roleOrUser) {
  return isObserver(roleOrUser) || isBookkeeper(roleOrUser);
}

/**
 * Can create/update clinic-scoped records: Organizer, Admin, Area Manager,
 * Practitioner Lead, Finance Coordinator.
 * Mirrors backend can_write_clinic_data (roles 1, 2, 3, 4, 9) — Finance
 * Coordinator (9) is included there for Cash Flow schedule payments. Keep the
 * two in sync.
 */
export function canWriteClinicData(roleOrUser) {
  return (
    isOrganizer(roleOrUser) ||
    isAdmin(roleOrUser) ||
    isAreaManager(roleOrUser) ||
    isPractitionerLead(roleOrUser) ||
    isFinanceCoordinator(roleOrUser)
  );
}

export function isManagerOrAbove(roleOrUser) {
  return isAreaManager(roleOrUser) || isAdmin(roleOrUser) || isOrganizer(roleOrUser);
}

/**
 * Roles that can open a clinic's report pages (/report/:clinicId — Daily Financial
 * Reporting, etc.). App.js uses this list for that route, so links elsewhere (e.g.
 * the Deposit Sheet's per-day report link) can be shown only to roles the route
 * will let through. Payroll and Finance Coordinator see the Deposit Sheet but not
 * clinic reports.
 */
export const CLINIC_REPORT_ROLES = [
  ROLE_IDS.ORGANIZER,
  ROLE_IDS.ADMIN,
  ROLE_IDS.AREA_MANAGER,
  ROLE_IDS.PRACTITIONER_LEAD,
  ROLE_IDS.OBSERVER,
  ROLE_IDS.BOOKKEEPER,
];

export function canViewClinicReport(roleOrUser) {
  return CLINIC_REPORT_ROLES.includes(getRoleId(roleOrUser));
}

/** Practitioner roster / directory: org/admin/AM/PL can edit; observer/bookkeeper read-only. */
export function canAccessPractitionerDirectory(roleOrUser) {
  return (
    isManagerOrAbove(roleOrUser) ||
    isPractitionerLead(roleOrUser) ||
    isObserver(roleOrUser) ||
    isBookkeeper(roleOrUser)
  );
}

/** Can edit practitioner directory entries (write). View-only roles excluded. */
export function canEditPractitionerDirectory(roleOrUser) {
  return canAccessPractitionerDirectory(roleOrUser) && !isViewOnlyRole(roleOrUser);
}

// ─── Schedule → Clinic closures & custom hours (per-resource write) ──────────
// Both live on the same ClinicVacationTab panel but hit different backend
// resources with different write sets, so they need separate gates.

/**
 * Clinic closure days: add / edit / delete a closure period.
 * Mirrors backend can_manage_clinic_closures (roles 1, 2, 3, 4) via
 * `ClinicClosureCRUDPermission`. Practitioner Lead (4) is included so it can
 * fully manage its own clinic's closures. Narrower than canWriteClinicData:
 * Finance Coordinator (9) has no scheduling role and is excluded on both sides.
 * Clinic confinement for roles 3/4 is the backend's job (payload check on POST,
 * object check on PATCH/DELETE) — this helper answers "may this role at all".
 */
export function canManageClinicClosures(roleOrUser) {
  return (
    isOrganizer(roleOrUser) ||
    isAdmin(roleOrUser) ||
    isAreaManager(roleOrUser) ||
    isPractitionerLead(roleOrUser)
  );
}

/**
 * Practitioner vacation requests: submit, approve/deny, edit, delete.
 * Mirrors backend can_manage_practitioner_vacations (roles 1, 2, 3, 4) on
 * `PractitionerVacationViewSet`. Practitioner Lead (4) approves for its own
 * clinic just like Area Manager (3) — the backend confines both.
 * Note DELETE resolves to 1, 2 only at the object level (see the backend
 * helper); this gate is about the approve/deny + edit affordances.
 */
export function canManagePractitionerVacations(roleOrUser) {
  return (
    isOrganizer(roleOrUser) ||
    isAdmin(roleOrUser) ||
    isAreaManager(roleOrUser) ||
    isPractitionerLead(roleOrUser)
  );
}

/**
 * Clinic schedule overrides (custom / reduced clinic hours on given dates).
 * Mirrors backend can_manage_clinic_schedule_overrides (roles 1, 2, 3) on
 * `ClinicScheduleOverrideViewSet`.
 * Deliberately excludes Practitioner Lead (4): the 2026-08-04 request covered
 * closure days only, so PL sees this section read-only rather than getting
 * buttons that 403. Note Area Manager (3) passes here but its DELETE still
 * fails at the object level — that resource uses `ClinicBasedCRUDPermission`,
 * which does not opt into clinic-scoped delete (pre-existing; see
 * .agents/features/2026-08-04-practitioner-lead-clinic-closures.md).
 */
export function canManageClinicScheduleOverrides(roleOrUser) {
  return isManagerOrAbove(roleOrUser);
}

/**
 * Finance pages (Journal Entry / Check / ADP Netsuite): can write/approve.
 * Mirrors backend can_act_on_payroll (roles 1, 2, 7, 9) via
 * `IsAdminOrganizerOrPayroll` — Finance Coordinator (9) writes the Finance
 * Operations tabs as well as Cash Flow transfers. Keep the two in sync.
 */
export function canEditFinance(roleOrUser) {
  return (
    isOrganizer(roleOrUser) ||
    isAdmin(roleOrUser) ||
    isPayroll(roleOrUser) ||
    isFinanceCoordinator(roleOrUser)
  );
}

/**
 * Finance pages: can view (read-only OK). Includes Area Manager, Observer,
 * Bookkeeper, and Finance Coordinator.
 * Mirrors backend can_view_payroll_finance (roles 1, 2, 3, 6, 7, 8, 9) — keep
 * the two in sync, or the page renders with its data query disabled.
 */
export function canViewFinance(roleOrUser) {
  return (
    canEditFinance(roleOrUser) ||
    isAreaManager(roleOrUser) ||
    isObserver(roleOrUser) ||
    isBookkeeper(roleOrUser) ||
    isFinanceCoordinator(roleOrUser)
  );
}

// ─── Cash Flow (per-tab write capability) ────────────────────────────────────
// The /cash-flow route is reachable by roles 1, 2, 7, 8, 9 (see App.js), but
// each tab hits a different backend resource with a different write set, so
// there is no single page-level "can edit". Payroll (7) and Bookkeeper (8)
// both write every Cash Flow tab, across all clinics — Bookkeeper is
// clinic-scoped and view-only everywhere ELSE in the app, which is why it has
// to be named per-helper here rather than folded into canWriteClinicData.

/**
 * Cash Flow → Schedule Payment writes (create/edit scheduled payments, plus the
 * statement payments / plans on the Monthly Inputs sub-tab and the grid's
 * move-occurrence action).
 *
 * Mirrors backend `can_manage_cash_flow` (1, 2, 3, 4, 7, 8, 9) via
 * `ClinicBasedCRUDWithPayrollPermission`. 3 and 4 are clinic-scoped; 7 and 8
 * are all-clinic.
 *
 * Payroll (7) and Bookkeeper (8) are `canWriteClinicData` PLUS this resource,
 * not part of it: the backend adds them per-resource for Cash Flow only, so
 * neither gains patients / reports / rosters. Keep that asymmetry — widening
 * `canWriteClinicData` instead would drift from the backend.
 */
export function canManageSchedulePayments(roleOrUser) {
  return canWriteClinicData(roleOrUser) || isPayroll(roleOrUser) || isBookkeeper(roleOrUser);
}

/**
 * Cash Flow → DELETING a scheduled payment. A strictly smaller set than
 * `canManageSchedulePayments`: 1, 2, 7, 8, 9 — Area Manager (3) and
 * Practitioner Lead (4) may create and edit within their clinics but may not
 * delete.
 *
 * Mirrors `ClinicBasedPermission.has_object_permission`, where a clinic-scoped
 * writer is refused DELETE unless the resource sets `allow_clinic_scoped_delete`
 * — and schedule payments do not. Separate from the edit check because deleting
 * takes the payment's whole history with it, cleared-occurrence records
 * included; the backend draws that line and the button has to draw it too, or
 * an area manager gets a 403 from a control that looked available.
 */
export function canDeleteSchedulePayments(roleOrUser) {
  return (
    isOrganizer(roleOrUser) ||
    isAdmin(roleOrUser) ||
    isPayroll(roleOrUser) ||
    isBookkeeper(roleOrUser) ||
    isFinanceCoordinator(roleOrUser)
  );
}

/**
 * Read bank-side data: daily bank balance, synced bank lines, saved deposit ↔
 * bank matches (the Bank columns on Deposit Sheet).
 * Mirrors backend can_view_daily_bank_balance (1, 2, 6, 7, 8, 9) via
 * `CanManageDailyBankBalance` (read side). Area Manager is not in it.
 */
export function canViewDailyBankBalance(roleOrUser) {
  return canManageDailyBankBalance(roleOrUser) || isObserver(roleOrUser);
}

/**
 * View Finance Operations → Daily Deposits (read only for 3 and 4, who see only
 * their own clinics). Mirrors backend can_view_daily_deposits
 * (1, 2, 3, 4, 6, 7, 8, 9) via `CanViewDailyDeposits`. Matching, accepting and
 * uploads stay canManageDailyBankBalance.
 */
export function canViewDailyDeposits(roleOrUser) {
  return (
    canViewDailyBankBalance(roleOrUser) ||
    isAreaManager(roleOrUser) ||
    isPractitionerLead(roleOrUser)
  );
}

/**
 * Cash Flow → Daily Bank Balance writes (balance / LOC / currency entry).
 * Mirrors backend can_manage_daily_bank_balance (1, 2, 7, 8, 9) via
 * `CanManageDailyBankBalance` — every role that reaches /cash-flow can write
 * here, Bookkeeper included.
 */
export function canManageDailyBankBalance(roleOrUser) {
  return (
    isOrganizer(roleOrUser) ||
    isAdmin(roleOrUser) ||
    isPayroll(roleOrUser) ||
    isBookkeeper(roleOrUser) ||
    isFinanceCoordinator(roleOrUser)
  );
}

/**
 * Cash Flow → transfer writes (create / approve / reject a transfer, post the
 * intercompany JE, move a transfer date). Mirrors backend can_act_on_payroll
 * (1, 2, 7, 9) via `IsAdminOrganizerOrPayroll`; Bookkeeper (8) can view
 * transfers but not act on them. Same set as `canEditFinance` — kept as its own
 * helper so a future change to either resource doesn't silently move the other.
 */
export function canManageTransfers(roleOrUser) {
  return canEditFinance(roleOrUser);
}

/** Lab invoices: view PDFs and AI suggestions. Mirrors backend can_view_lab_invoices. */
export function canViewLabInvoices(roleOrUser) {
  return (
    isOrganizer(roleOrUser) ||
    isAdmin(roleOrUser) ||
    isAreaManager(roleOrUser) ||
    isPractitionerLead(roleOrUser) ||
    isObserver(roleOrUser) ||
    isPayroll(roleOrUser) ||
    isBookkeeper(roleOrUser)
  );
}

/** Lab invoices: clinic-level actions such as retry, discard, edit breakdowns, and assign practitioner/date. */
export function canManageLabInvoices(roleOrUser) {
  return (
    isOrganizer(roleOrUser) ||
    isAdmin(roleOrUser) ||
    isAreaManager(roleOrUser) ||
    isPractitionerLead(roleOrUser) ||
    isPayroll(roleOrUser)
  );
}

/** Lab invoices: re-run AI scan from stored PDF. Organizer/Admin only. */
export function canRescanLabInvoices(roleOrUser) {
  return isOrganizer(roleOrUser) || isAdmin(roleOrUser);
}

/**
 * Can act in the 3-step approval workflow (Area Manager → Payroll → Admin) at
 * all. This is the gate for *showing* the approval pills and reset controls;
 * WHICH step a user may click is decided per-step by `canApproveStep` in
 * AssociateFee, which matches the step's role exactly.
 *
 * Payroll (7) belongs here because it owns the payroll_approval step — see the
 * backend's APPROVAL_FIELD_BY_ROLE. It is intentionally a wider set than the
 * backend's `can_approve_compensation` (1, 2, 3), which guards a different
 * thing: bulk admin sign-off.
 */
export function canApproveCompensation(roleOrUser) {
  return (
    isOrganizer(roleOrUser) ||
    isAdmin(roleOrUser) ||
    isAreaManager(roleOrUser) ||
    isPayroll(roleOrUser)
  );
}

/**
 * Email an associate fee PDF to the practitioner — the per-row envelope button
 * and "Send All Emails". Mirrors backend `can_email_compensation_pdf`
 * (1, 2, 3, 7).
 *
 * Payroll (7) sends the statements as its core job. Kept as its own helper
 * rather than reusing `canApproveCompensation` so that changing who may approve
 * does not silently change who may mail practitioners.
 */
export function canEmailCompensationPdf(roleOrUser) {
  return (
    isOrganizer(roleOrUser) ||
    isAdmin(roleOrUser) ||
    isAreaManager(roleOrUser) ||
    isPayroll(roleOrUser)
  );
}

/**
 * Compensation PDF download / preview. Mirrors backend
 * `can_view_compensation_pdf`.
 *
 * The SAME audience that can read the associate-fee table, because the PDF only
 * reformats a row already on screen. It used to be `canApproveCompensation ||
 * isBookkeeper`, which left Payroll seeing the table with no way to open its PDF —
 * and Practitioner Lead, Observer and Finance Coordinator in the same position.
 *
 * Emailing a PDF is NOT this: see `canEmailCompensationPdf`, since sending a
 * practitioner their compensation is an outward-facing act, not a read.
 */
export function canDownloadCompensationPdf(roleOrUser) {
  // The backend union is roles 1,2,3,4,6,7,8,9 — everyone but visitor. Written as
  // these two predicates rather than `!isVisitor` so an unrecognised role is
  // denied by default instead of falling through to access.
  return canViewFinance(roleOrUser) || isPractitionerLead(roleOrUser);
}

/**
 * Roles an actor is allowed to assign (mirrors backend `can_assign_role`).
 * - Organizer: any role
 * - Admin: any role except Organizer
 * - Others: cannot assign roles
 */
export function getAssignableRoleOptions(actor) {
  const all = [
    { value: 1, label: "Organizer" },
    { value: 2, label: "Admin" },
    { value: 3, label: "Area Manager" },
    { value: 4, label: "Practitioner Lead" },
    { value: 5, label: "Visitor" },
    { value: 6, label: "Observer" },
    { value: 7, label: "Payroll" },
    { value: 8, label: "Bookkeeper" },
    { value: 9, label: "Finance Coordinator" },
  ];
  if (isOrganizer(actor)) return all;
  if (isAdmin(actor)) return all.filter((r) => r.value !== 1);
  return [];
}

export function getDefaultRouteForUser(user) {
  if (isPractitionerLead(user)) {
    const clinic = user?.clinic?.[0];
    return clinic ? `/report/${clinic}` : "/report";
  }
  if (isAreaManager(user)) return "/management";
  if (isBookkeeper(user)) return "/management";
  if (isObserver(user)) return "/admin";
  if (isPayroll(user)) return "/finance-operations";
  if (isFinanceCoordinator(user)) return "/cash-flow";
  if (isVisitor(user)) return "/report";
  return "/admin";
}
